import { InspectorControls, useBlockProps, BlockControls } from "@wordpress/block-editor";
import { Button, ButtonGroup, Disabled, PanelBody, SelectControl, TextControl, TextareaControl, ToggleControl, ToolbarGroup, ToolbarDropdownMenu } from "@wordpress/components";
import { useEffect, useRef } from "@wordpress/element";
import { useSelect } from "@wordpress/data";
import { __, sprintf } from "@wordpress/i18n";
import { isDuplicateTocAnchor } from "../../../shared/js/tocAnchorDiagnostics";
import DebouncedServerSideRender from "../../../shared/js/DebouncedServerSideRender";
import ProductRefsControl from "../../../shared/js/ProductRefsControl";
import useCeggEditorProducts from "../../../shared/js/useCeggEditorProducts";

const VARIANT_OPTIONS = [
    { label: __("Default", "content-egg"), value: "default" },
    { label: __("Versus", "content-egg"), value: "versus" },
    { label: __("Product List", "content-egg"), value: "product-list" },
];

const COLOR_SCHEME_OPTIONS = [
    { label: __("Auto (site default)", "content-egg"), value: "auto" },
    { label: __("Light", "content-egg"), value: "light" },
    { label: __("Dark", "content-egg"), value: "dark" },
];

const CRITERION_TYPE_OPTIONS = [
    { label: __("Text", "content-egg"), value: "text" },
    { label: __("Score", "content-egg"), value: "score" },
    { label: __("Star", "content-egg"), value: "star" },
    { label: __("Boolean", "content-egg"), value: "boolean" },
    { label: __("Price", "content-egg"), value: "price" },
];

function createItemId() {
    return `ct-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function parseValues(value) {
    return String(value || "").split("\n").map((line) => line.trim());
}

function formatValues(values = []) {
    return (values || []).map((value) => {
        if (typeof value === "boolean") {
            return value ? "true" : "false";
        }

        return String(value ?? "");
    }).join("\n");
}

function getCriterionValuesHelp(type) {
    switch (type) {
        case "score":
            return __("One value per line, in current product order. Use plain numbers like 9.2 or full text like 9.2 / 10. For price rows, values are ignored.", "content-egg");
        case "star":
            return __("One value per line, in current product order. Supports a simple 3-level scale: high (3 stars), medium (2 stars), low (1 star). Use values like 3, 2, 1 or words like best, good, low. For price rows, values are ignored.", "content-egg");
        case "boolean":
            return __("One value per line, in current product order. Supports yes/true/1/y for positive, no/false/0/n for negative, and empty for a neutral dash. For price rows, values are ignored.", "content-egg");
        default:
            return __("One value per line, in current product order. For price rows, values are ignored.", "content-egg");
    }
}

export default function Edit({ attributes, setAttributes }) {
    const blockProps = useBlockProps({ className: attributes.align ? `align${attributes.align}` : "" });
    const snapshot = useCeggEditorProducts();
    const {
        variant,
        heading_label,
        heading_title,
        heading_tag,
        cta_label,
        footer_note,
        items = [],
        criteria = [],
        anchor,
        toc_label,
        include_in_toc,
        level,
        color_scheme,
    } = attributes;
    const allBlocks = useSelect((select) => select("core/block-editor").getBlocks(), []);
    const hasDuplicateTocAnchor = !!include_in_toc && isDuplicateTocAnchor(allBlocks, anchor);
    const suggestedTocLabel = (heading_title || heading_label || "").trim();

    const seededValuesRef = useRef({});

    useEffect(() => {
        const nextItems = [...(items || [])];
        const nextSeededValues = {};
        let hasChanges = false;

        nextItems.forEach((item, index) => {
            const itemId = item?.id || createItemId();
            if (!item?.id) {
                nextItems[index] = { ...item, id: itemId };
                hasChanges = true;
            }

            const ref = item?.product_ref || {};
            const productKey = `${String(ref.module_id || "")}:${String(ref.unique_id || "")}`;
            const product = (snapshot.all || []).find((candidate) => (
                `${String(candidate.module_id || "")}:${String(candidate.unique_id || "")}` === productKey
            )) || null;

            if (!product || productKey === ":") {
                return;
            }

            const previousSeeded = seededValuesRef.current[itemId] || {
                productKey: "",
                title: "",
            };
            const nextSeeded = {
                productKey,
                title: String(product.title || "").trim(),
            };
            nextSeededValues[itemId] = nextSeeded;

            if (previousSeeded.productKey === productKey) {
                return;
            }

            const currentTitle = String(nextItems[index]?.title || "").trim();
            const nextPatch = {};

            if (nextSeeded.title !== "" && (currentTitle === "" || currentTitle === previousSeeded.title)) {
                nextPatch.title = nextSeeded.title;
            }

            if (Object.keys(nextPatch).length > 0) {
                nextItems[index] = {
                    ...nextItems[index],
                    ...nextPatch,
                };
                hasChanges = true;
            }
        });

        seededValuesRef.current = nextSeededValues;

        if (hasChanges) {
            setAttributes({ items: nextItems });
        }
    }, [items, setAttributes, snapshot.all]);

    const updateItems = (nextItems) => setAttributes({ items: nextItems });
    const updateCriteria = (nextCriteria) => setAttributes({ criteria: nextCriteria });
    const hasPreviewContent = Array.isArray(items) && items.length > 0;
    const criteriaPanelTitle = variant === "product-list"
        ? __("Columns", "content-egg")
        : __("Criteria Rows", "content-egg");
    const criterionTypeOptions = CRITERION_TYPE_OPTIONS;

    return (
        <>
            <BlockControls>
                <ToolbarGroup>
                    <ToolbarDropdownMenu
                        icon="screenoptions"
                        label={__("Variant", "content-egg")}
                        text={VARIANT_OPTIONS.find((o) => o.value === variant)?.label}
                        controls={VARIANT_OPTIONS.map((option) => ({
                            title: option.label,
                            isActive: variant === option.value,
                            onClick: () => setAttributes({ variant: option.value }),
                        }))}
                    />
                </ToolbarGroup>
            </BlockControls>

            <InspectorControls>
                <PanelBody title={__("Layout", "content-egg")} initialOpen={true}>
                    <SelectControl
                        label={__("Variant", "content-egg")}
                        value={variant}
                        options={VARIANT_OPTIONS}
                        onChange={(value) => setAttributes({ variant: value })}
                    />
                    <SelectControl
                        label={__("Color Scheme", "content-egg")}
                        value={color_scheme}
                        options={COLOR_SCHEME_OPTIONS}
                        onChange={(value) => setAttributes({ color_scheme: value })}
                        __nextHasNoMarginBottom
                    />
                </PanelBody>

                <PanelBody title={__("Product Binding", "content-egg")} initialOpen={!hasPreviewContent}>
                    <ProductRefsControl
                        items={items}
                        onChange={updateItems}
                        preserveUnboundRows
                        emptyMessage={__("No comparison products are currently bound to this block.", "content-egg")}
                        createEmptyItem={(productRef) => ({
                            id: createItemId(),
                            product_ref: productRef,
                            title: "",
                            role_label: "",
                            is_winner: false,
                        })}
                        renderItemFields={({ item, updateItem }) => (
                            <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                                <TextControl
                                    label={__("Title", "content-egg")}
                                    value={item?.title || ""}
                                    onChange={(value) => updateItem((currentItem) => ({
                                        ...currentItem,
                                        title: value,
                                    }))}
                                />
                                <TextControl
                                    label={__("Role Label", "content-egg")}
                                    value={item?.role_label || ""}
                                    onChange={(value) => updateItem((currentItem) => ({
                                        ...currentItem,
                                        role_label: value,
                                    }))}
                                />
                                <ToggleControl
                                    label={__("Winner", "content-egg")}
                                    checked={!!item?.is_winner}
                                    onChange={(value) => updateItem((currentItem) => ({
                                        ...currentItem,
                                        is_winner: value,
                                    }))}
                                />
                            </div>
                        )}
                    />
                </PanelBody>

                <PanelBody title={__("Content", "content-egg")} initialOpen={true}>
                    <TextControl
                        label={__("Heading Label", "content-egg")}
                        value={heading_label}
                        onChange={(value) => setAttributes({ heading_label: value })}
                    />
                    <TextControl
                        label={__("Heading Title", "content-egg")}
                        value={heading_title}
                        onChange={(value) => setAttributes({ heading_title: value })}
                    />
                    <div style={{ marginBottom: "8px" }}>
                        <div style={{ fontSize: "11px", fontWeight: 500, marginBottom: "6px" }}>
                            {__("Heading Tag", "content-egg")}
                        </div>
                        <ButtonGroup>
                            {["div", "h1", "h2", "h3", "h4"].map((tag) => (
                                <Button
                                    key={tag}
                                    variant={heading_tag === tag ? "primary" : "secondary"}
                                    size="small"
                                    onClick={() => setAttributes({ heading_tag: tag })}
                                >
                                    {tag.toUpperCase()}
                                </Button>
                            ))}
                        </ButtonGroup>
                    </div>
                    <TextControl
                        label={__("CTA Label", "content-egg")}
                        value={cta_label}
                        onChange={(value) => setAttributes({ cta_label: value })}
                    />
                    {variant !== "product-list" ? (
                        <TextareaControl
                            label={__("Footer Note", "content-egg")}
                            value={footer_note}
                            onChange={(value) => setAttributes({ footer_note: value })}
                            rows={2}
                            __nextHasNoMarginBottom
                        />
                    ) : null}
                </PanelBody>

                <PanelBody title={criteriaPanelTitle} initialOpen={false}>
                        {(criteria || []).map((criterion, index) => (
                            <div
                                key={index}
                                style={{
                                    marginBottom: index === criteria.length - 1 ? 0 : "12px",
                                    paddingBottom: index === criteria.length - 1 ? 0 : "12px",
                                    borderBottom: index === criteria.length - 1 ? "none" : "1px solid #e0e0e0",
                                }}
                            >
                                <TextControl
                                    label={__("Label", "content-egg")}
                                    value={criterion?.label || ""}
                                    onChange={(value) => {
                                        const next = [...(criteria || [])];
                                        next[index] = { ...(next[index] || {}), label: value };
                                        updateCriteria(next);
                                    }}
                                />
                                <SelectControl
                                    label={__("Type", "content-egg")}
                                    value={criterion?.type || "text"}
                                    options={criterionTypeOptions}
                                    onChange={(value) => {
                                        const next = [...(criteria || [])];
                                        next[index] = { ...(next[index] || {}), type: value };
                                        updateCriteria(next);
                                    }}
                                />
                                {(criterion?.type || "text") !== "price" ? (
                                    <TextareaControl
                                        label={__("Values", "content-egg")}
                                        help={getCriterionValuesHelp(criterion?.type || "text")}
                                        value={formatValues(criterion?.values || [])}
                                        onChange={(value) => {
                                            const next = [...(criteria || [])];
                                            next[index] = { ...(next[index] || {}), values: parseValues(value) };
                                            updateCriteria(next);
                                        }}
                                        rows={4}
                                    />
                                ) : null}
                                <div style={{ display: "flex", justifyContent: "flex-end" }}>
                                    <Button
                                        icon="no-alt"
                                        isDestructive
                                        size="small"
                                        onClick={() => {
                                            const next = [...(criteria || [])];
                                            next.splice(index, 1);
                                            updateCriteria(next);
                                        }}
                                    >
                                        {variant === "product-list" ? __("Remove column", "content-egg") : __("Remove row", "content-egg")}
                                    </Button>
                                </div>
                            </div>
                        ))}
                        <Button
                            variant="secondary"
                            size="small"
                            onClick={() => updateCriteria([
                                ...(criteria || []),
                                { label: "", type: "text", values: [] },
                            ])}
                        >
                            {variant === "product-list" ? __("Add column", "content-egg") : __("Add row", "content-egg")}
                        </Button>
                </PanelBody>

                <PanelBody title={__("TOC", "content-egg")} initialOpen={false}>
                    <TextControl
                        label={__("Anchor", "content-egg")}
                        value={anchor || ""}
                        onChange={(value) => setAttributes({ anchor: value })}
                    />
                    {hasDuplicateTocAnchor && (
                        <p style={{ marginTop: "-4px", marginBottom: "12px", color: "#8a1f11", fontSize: "12px" }}>
                            {__("This anchor is already used by another TOC block.", "content-egg")}
                        </p>
                    )}
                    <ToggleControl
                        label={__("Include In TOC", "content-egg")}
                        checked={!!include_in_toc}
                        onChange={(value) => setAttributes({ include_in_toc: value })}
                    />
                    {include_in_toc && !(anchor || "").trim() && (
                        <p style={{ marginTop: "-4px", marginBottom: "12px", color: "#757575", fontSize: "12px" }}>
                            {__("Set an anchor so the TOC can link to this block.", "content-egg")}
                        </p>
                    )}
                    <TextControl
                        label={__("TOC Label", "content-egg")}
                        value={toc_label || ""}
                        onChange={(value) => setAttributes({ toc_label: value })}
                    />
                    {include_in_toc && !(toc_label || "").trim() && (
                        <p style={{ marginTop: "-4px", marginBottom: "12px", color: "#757575", fontSize: "12px" }}>
                            {suggestedTocLabel
                                ? sprintf(__("Suggested label: %s", "content-egg"), `"${suggestedTocLabel}"`)
                                : __("Set a TOC label so this block can appear in automatic TOC mode.", "content-egg")}
                        </p>
                    )}
                    <TextControl
                        label={__("Level", "content-egg")}
                        type="number"
                        min={1}
                        max={6}
                        value={level ?? 2}
                        onChange={(value) => setAttributes({ level: value === "" ? 2 : Number(value) })}
                        __nextHasNoMarginBottom
                    />
                </PanelBody>
            </InspectorControls>

            <div {...blockProps}>
                {hasPreviewContent ? (
                    <Disabled>
                        <DebouncedServerSideRender
                            block="eggb/comparison-table"
                            attributes={attributes}
                        />
                    </Disabled>
                ) : (
                    <p style={{ color: "#999", fontStyle: "italic", padding: "16px", margin: 0, fontSize: "13px" }}>
                        {__("Bind Content Egg products to preview this block.", "content-egg")}
                    </p>
                )}
            </div>
        </>
    );
}
